import Cookies from "js-cookie";
import posthog from "posthog-js";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Header from "@components/elements/Header";
import * as ConsultationComp from "@components/layouts/StartConsultationPage";
import { useAuth } from "@contexts/auth/AuthProvider";
import useSearchPartner from "@hooks/admin/partners/useSearchPartner";
import useValidatePromo from "@hooks/billing/useValidatePromo";
import useFindConditions from "@hooks/case/useFindCondition";
import useQuestionnaireIndex from "@hooks/questionnaire/useQuestionnaireIndex";
import useAmplitude from "@hooks/useAmplitude";
import useDataLayer from "@hooks/useDataLayer";
import { useQuery } from "@hooks/useQuery";
import { useTitle } from "@hooks/useTitle";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
  Feedback,
  FormGroup,
  FormGroupMessage,
  Text,
  TextField,
} from "@runwayhealth/runway-components-react";
import { CREATE_CASE } from "@store/cases/casesActions";
import { setConditions } from "@store/cases/casesSlice";
import { clearForm, loadForm } from "@store/questionnaire/questionnaireSlice";
import { COLLECT_MARKETING_EMAIL } from "@store/user/userActions";
import { userUpdate } from "@store/user/userSlice";
import { GA4Events } from "@types-runway/analytics";
import { Case } from "@types-runway/case";
import { ConditionTypes } from "@types-runway/condition";
import { PricesState, RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";
import { markConditionsAsCartPreselected } from "@utils/cart/cartPreselection";
import { mapConditionPrescriptions } from "@utils/case/conditionPrescriptions";
import { validateEmail } from "@utils/forms/utilsFormvalidation";

import QuestionnairePage from "./Components/QuestionnairePage";
import { decideConsultationInit } from "./initConsultation";

// Lazy components
const Cart = lazy(() => import("@components/Cart/index"));

type CartItem = {
  stripe_id: string;
  priority: number;
};

type CartCookie = {
  items: CartItem[];
  country: string;
  transaction: string;
  referral?: string;
};

const parseCartItems = (
  cookie: CartCookie
): { stripeIds: string[]; priorityMap: Map<string, number> } => {
  const priorityMap = new Map<string, number>();
  const stripeIds = cookie.items.map((item) => {
    priorityMap.set(item.stripe_id, item.priority);
    return item.stripe_id;
  });
  return { stripeIds, priorityMap };
};

const Consultation = () => {
  useTitle("Start Consultation");

  // Hooks declarations.
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();
  const { logEvent } = useAmplitude();
  const { isAuth } = useAuth();
  // Conditions hook.
  const { findConditionByName, findConditionsByPrices } = useFindConditions();
  const { reportPurchasedProducts } = useDataLayer(GA4Events.START_CONSULTATION);
  const { reportPurchaseLead } = useDataLayer(GA4Events.GENERATE_LEAD);
  // End of hook declarations

  // Email form state.
  const [errorText, setErrorText] = useState("");
  const [email, setEmail] = useState("");
  const [emailAgree, setEmailAgree] = useState(false);
  const [emailPrefilled, setEmailPrefilled] = useState(false);

  // Questionnaire state.
  const [isStreamlinedFlow, setIsStreamlinedFlow] = useState(false);
  const [startedQuestionnaire, setStartedQuestionnaire] = useState(false);
  const { readIndex, clearIndex } = useQuestionnaireIndex();

  // Consultation discount state.
  const { searchPartnerByPromocode, partner } = useSearchPartner();
  const { validatePromo, isValidPromo, promoDetails } = useValidatePromo();

  // Prices state.
  const prices = useSelector<RootState, PricesState>((state) => state.price);
  const visitFeeDiscount: number | undefined = useMemo(() => {
    let discount = undefined;

    if (promoDetails?.coupon.amount_off) {
      discount = promoDetails?.coupon.amount_off / 100;
    }

    if (promoDetails?.coupon.percent_off) {
      discount = prices.case.data.unit_amount * ((promoDetails?.coupon.percent_off ?? 0) / 100);
    }
    return discount;
  }, [promoDetails]);

  // Cookie loaded values.
  const cartCookie = JSON.parse(Cookies.get("cart") ?? "{}") as CartCookie;
  const hasCartData = Object.keys(cartCookie).length > 0;
  const { country = "", transaction = "" } = hasCartData
    ? cartCookie
    : { country: "", transaction: "" };
  const { stripeIds, priorityMap } = hasCartData && cartCookie.items?.length > 0
    ? parseCartItems(cartCookie)
    : { stripeIds: [] as string[], priorityMap: new Map<string, number>() };
  // Redux state.
  const newCase = useSelector<RootState, RootEntity<Case>>((state) => state.case.newCase);
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);

  useEffect(() => {
    if (!user.data.email || email) return;
    setEmail(user.data.email);
    setEmailPrefilled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.data.email]);

  useEffect(() => {
    // Logs consultation start to amplitude.
    logEvent("CONSULTATION_LANDING");

    // If a user comes from a transaction, we set the transaction state.
    if (transaction !== "") {
      dispatch(userUpdate({ name: "partnerTx", value: transaction }));
    }

    // Loads query params.
    let lastSavedIndex: number = JSON.parse(query.get("currentIndex") || "null") ?? readIndex();
    const startQuestionnaire = JSON.parse(query.get("startQuestionnaire") ?? "false");

    const currentFlow = sessionStorage.getItem("flow") ?? "none";

    if (Boolean(query.get("flow"))) {
      sessionStorage.setItem("flow", query.get("flow") ?? currentFlow);
    }

    const { shouldResetForNewCase } = decideConsultationInit(newCase.data.id);

    // If there is not a loaded case, create one.
    if (shouldResetForNewCase) {
      dispatch({
        type: CREATE_CASE,
        newCase: { ...newCase.data, UserId: user.data.id !== "" ? user.data.id : null },
      });
      // Then, clear the questionnaire form and any saved progress. We do NOT
      // clear driver_license_id here: an uploaded guardian ID must survive an
      // empty-session / second-tab boot (ZD-3226 / ZD-3209).
      dispatch(clearForm());
      clearIndex();
    } else {
      // Load and startquestionnaire.
      dispatch(loadForm(newCase.data.case_questions ?? []));
    }

    if (lastSavedIndex || startQuestionnaire || newCase.data.case_questions?.length > 0) {
      setStartedQuestionnaire(true);
    }

    const flow = sessionStorage.getItem("flow");
    setIsStreamlinedFlow(flow === "streamlined");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load conditions.
    if (stripeIds.length > 0) {
      (async () => {
        const conditionPrices = await findConditionsByPrices(stripeIds);

        // Bridge: Stripe price ID -> condition product name
        const conditionPriorityMap = new Map<string, number>();
        for (const price of conditionPrices) {
          const priority = priorityMap.get(price.id);
          if (priority !== undefined) {
            conditionPriorityMap.set(price.product.name, priority);
          }
        }

        const conditionNames = conditionPrices.map(
          (price) => price.product.name
        ) as ConditionTypes[];
        const conditions = (
          await Promise.all(
            conditionNames.map((conditionName: string) => {
              return findConditionByName(conditionName as ConditionTypes);
            })
          )
        ).filter(Boolean);
        const mappedConditions = await mapConditionPrescriptions(
          conditions,
          0,
          conditionPriorityMap
        );

        markConditionsAsCartPreselected(
          mappedConditions.map((c) => c.product.name)
        );
        dispatch(setConditions({ conditions: mappedConditions }));
      })();
    }

    // Load discount if any.
    let discountCookie: string | null = null;
    discountCookie = Cookies.get("promo-code") ?? null;

    if (discountCookie) {
      (async () => {
        const promocode = discountCookie;
        const foundPartners = await searchPartnerByPromocode(promocode);

        if (foundPartners.length > 0) {
          await validatePromo(promocode);

          if (!isValidPromo) {
            return;
          }
        }
      })();
    }
  }, []);

  const startConsultation = async () => {
    if (errorText.length !== 0 && email.length === 0) {
      return;
    }

    dispatch({
      type: COLLECT_MARKETING_EMAIL,
      email,
      subscribeToNewsLetter: emailAgree,
      discountBody: partner
        ? {
            promoCode: promoDetails?.code,
            partner,
          }
        : undefined,
    });
    // Should log consultation event to amplitude.
    posthog.capture("email_submitted", { email: email.toLowerCase() });
    // posthog.identify(email.toLowerCase()); TO DO IF POSTHOG RECOMMENDS!

    if (!emailPrefilled) reportPurchaseLead();
    // Report start consultation event with purchased products.
    reportPurchasedProducts();
    setStartedQuestionnaire(true);
  };

  const handleEmailFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorText("");
    setEmail(e.target.value);
    if (!validateEmail(e.target.value)) {
      return setErrorText("Please enter your email address");
    }
    dispatch(userUpdate({ name: "email", value: e.target.value }));
  };

  return (
    <>
      {startedQuestionnaire ? (
        <QuestionnairePage
          startQuestionnaire={setStartedQuestionnaire}
          partner={partner}
          visitFeeDiscount={visitFeeDiscount}
        />
      ) : (
        <>
          <Header hasProgressBar={startedQuestionnaire} hideBackButton={!startedQuestionnaire} />
          <ConsultationComp.StartConsultationPage
            tabIndex={1}
            onKeyUp={(e: React.KeyboardEvent<HTMLElement>) =>
              e.key == "Enter" && startConsultation()
            }
          >
            <ConsultationComp.MainContent>
              <ConsultationComp.ContentInfo>
                <ConsultationComp.RunwayRxText />
                {!isAuth && !emailPrefilled && (
                  <div className="form-content">
                    <Text>Enter your email below to get started!</Text>
                    <FormGroup hasValidationField>
                      <TextField
                        data-testid="marketing-email-input"
                        id="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={handleEmailFieldChange}
                        onBlur={handleEmailFieldChange}
                        isInvalid={errorText !== ""}
                        type="email"
                      />
                      <FormGroupMessage>
                        <Feedback size="sm" isInvalid>
                          {errorText}
                        </Feedback>
                      </FormGroupMessage>
                    </FormGroup>
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            data-testid="email-marketing-checkbox"
                            color="default"
                            checked={emailAgree}
                            onChange={() => setEmailAgree(!emailAgree)}
                          />
                        }
                        label={<ConsultationComp.ConsentText />}
                      />
                    </div>
                  </div>
                )}
              </ConsultationComp.ContentInfo>
              {!isStreamlinedFlow && (
                <ConsultationComp.CartWrapper>
                  <div className="cart-width">
                    <Suspense fallback={<div>Loading...</div>}>
                      <Cart
                        visitFeeDiscount={visitFeeDiscount}
                        country={country}
                        variant="consultation"
                      />
                    </Suspense>
                  </div>
                </ConsultationComp.CartWrapper>
              )}
              <ConsultationComp.ContentInfo>
                <div className="start-button-wrapper">
                  <ConsultationComp.StartButton
                    data-testid="start-consultation-button"
                    size="lg"
                    onClick={() => startConsultation()}
                    iconName="ArrowRight"
                    isLoading={newCase.isPending}
                    disabled={(errorText.length > 0 || email === "") && !isAuth}
                    isBlock
                  >
                    Start my visit
                  </ConsultationComp.StartButton>
                  {!isAuth && (
                    <ConsultationComp.MemberLogin
                      onClick={() => history.push("/login?startQuestionnaire=true")}
                    />
                  )}
                </div>
              </ConsultationComp.ContentInfo>
            </ConsultationComp.MainContent>
          </ConsultationComp.StartConsultationPage>
        </>
      )}
    </>
  );
};

export default Consultation;
